/**
 * Invite — Einladungs-Annahme-Seite für neue Benutzer.
 *
 * Features:
 * - Token-Validierung und Einladungs-Details anzeigen
 * - Registrierungsformular (Vorname, Nachname, Passwort)
 * - Automatischer Login nach erfolgreicher Registrierung
 *
 * @page /invite/:token (unauthentifiziert)
 * @roles keine (öffentlich, Token-basiert)
 * @i18n auth
 * @endpoints GET /api/einladungen/:token, POST /api/einladungen/:token/accept
 */
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';

const API = import.meta.env.VITE_API_URL || '';

export default function Invite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const { t } = useLanguage();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ vorname: '', nachname: '', password: '', password2: '' });

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/einladungen/${token}`);
        if (!res.ok) { setError(t('auth.inviteInvalid')); return; }
        const data = await res.json();
        setInvite(data);
      } catch { setError(t('auth.inviteLoadError')); }
      finally { setLoading(false); }
    })();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password !== form.password2) { setError(t('auth.passwordsMismatch')); return; }
    if (form.password.length < 8) { setError(t('auth.passwordMinLength')); return; }

    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/einladungen/${token}/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ vorname: form.vorname, nachname: form.nachname, password: form.password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      await checkAuth();
      navigate('/');
    } catch (err) { setError(err.message); }
    finally { setSubmitting(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-2 border-[#338edc] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error && !invite) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center px-4">
        <div className="text-center">
          <div className="w-14 h-14 rounded-card bg-red-500/10 flex items-center justify-center mx-auto mb-4">
            <svg className="w-7 h-7 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" /></svg>
          </div>
          <h2 className="text-base font-medium text-content mb-2">{t('auth.inviteInvalidTitle')}</h2>
          <p className="text-sm text-[var(--text-secondary)]">{error}</p>
        </div>
      </div>
    );
  }

  const inp = "w-full bg-surface-inset border border-line-input rounded-lg px-3 py-2.5 text-sm text-content placeholder-content-dim focus:outline-none focus:border-[#338edc]/50 focus:ring-1 focus:ring-primary-500/30 transition-colors";

  return (
    <div className="min-h-screen bg-surface flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center mb-8">
          <div className="w-14 h-14 rounded-card bg-gradient-to-br from-[#338edc] to-purple-600 flex items-center justify-center mb-4">
            <span className="text-white font-extrabold text-xl">W</span>
          </div>
          <h1 className="text-xl font-bold text-content">{t('auth.createAccount')}</h1>
          <p className="text-sm text-[var(--text-secondary)] mt-1">{t('auth.inviteFor')} <span className="text-[var(--text-primary)]">{invite?.email}</span></p>
        </div>

        <form onSubmit={handleSubmit} className="bg-surface-card border border-line-card rounded-card p-6 space-y-4 shadow-card">
          {error && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-2.5 text-sm text-red-400">{error}</div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">{t('auth.firstName')}</label>
              <input type="text" value={form.vorname} onChange={e => setForm(f => ({ ...f, vorname: e.target.value }))} className={inp} required autoFocus />
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">{t('auth.lastName')}</label>
              <input type="text" value={form.nachname} onChange={e => setForm(f => ({ ...f, nachname: e.target.value }))} className={inp} required />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">{t('auth.password')}</label>
            <input type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} className={inp} placeholder={t('auth.minChars')} required autoComplete="new-password" />
          </div>

          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">{t('auth.confirmPassword')}</label>
            <input type="password" value={form.password2} onChange={e => setForm(f => ({ ...f, password2: e.target.value }))} className={inp} required autoComplete="new-password" />
          </div>

          <button type="submit" disabled={submitting} className="w-full bg-[#338edc] hover:bg-[#2b7bc4] disabled:bg-[#338edc]/50 text-white font-medium text-sm py-2.5 rounded-lg transition-colors">
            {submitting ? t('auth.creatingAccount') : t('auth.createAccount')}
          </button>
        </form>
      </div>
    </div>
  );
}
